import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { GuestAuthProvider } from './contexts/GuestAuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import EmailPopup from './components/EmailPopup';
import SEOHead from './components/SEOHead';
import Home from './pages/Home';
import Properties from './pages/Properties';
import PropertyDetail from './pages/PropertyDetail';
import BookNow from './pages/BookNow';
import BookingWidget from './pages/BookingWidget';
import BookingConfirmation from './pages/BookingConfirmation';
import About from './pages/About';
import LocalGuide from './pages/LocalGuide';
import PropertyLocalGuide from './pages/PropertyLocalGuide';
import HouseRules from './pages/HouseRules';
import Contact from './pages/Contact';
import Blog from './pages/Blog';
import AuthCallback from './pages/AuthCallback';
import { Toaster } from './components/ui/toaster';

// Location Pages
import LocationsIndex from './pages/locations/LocationsIndex';
import LocationPage from './pages/locations/LocationPage';
import PropertyQRCodes from './pages/PropertyQRCodes';
import GuestResources from './pages/GuestResources';
import Favorites from './pages/Favorites';
import PrivacyPolicy from './pages/PrivacyPolicy';
import QRSigns from './pages/QRSigns';
import GuestHub from './pages/guest-hub/GuestHub';
import WifiPage from './pages/guest-hub/WifiPage';
import HubGuidebook from './pages/guest-hub/HubGuidebook';
import HubLocalGuide from './pages/guest-hub/HubLocalGuide';
import SocialPage from './pages/guest-hub/SocialPage';
import BookPage from './pages/guest-hub/BookPage';
import { slugForId } from './pages/guest-hub/hubConfig';
import AdminWifi from './admin/AdminWifi';

const LegacyGuidebookRedirect = () => {
  const { propertyId } = useParams();
  return <Navigate to={`/${slugForId(propertyId)}/guidebook`} replace />;
};

// Admin imports
import AdminLogin from './admin/AdminLogin';
import AdminLayout from './admin/AdminLayout';
import AdminDashboard from './admin/AdminDashboard';
import AdminProperties from './admin/AdminProperties';
import AdminBlog from './admin/AdminBlog';
import AdminSubscribers from './admin/AdminSubscribers';
import AdminContacts from './admin/AdminContacts';
import AdminSEO from './admin/AdminSEO';
import AdminGuidebooks from './admin/AdminGuidebooks';
import AdminGuidebookEditor from './admin/AdminGuidebookEditor';

/**
 * AppRouter - Handles OAuth callback detection before normal routing
 * CRITICAL: Detect session_id synchronously during render to prevent race conditions
 */
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment (not query params) for session_id - must use useLocation().hash for reactivity
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="properties" element={<AdminProperties />} />
        <Route path="blog" element={<AdminBlog />} />
        <Route path="subscribers" element={<AdminSubscribers />} />
        <Route path="contacts" element={<AdminContacts />} />
        <Route path="seo" element={<AdminSEO />} />
        <Route path="wifi" element={<AdminWifi />} />
        <Route path="guidebooks" element={<AdminGuidebooks />} />
        <Route path="guidebooks/:propertyId" element={<AdminGuidebookEditor />} />
      </Route>

      {/* Guest Hub (mobile-first, no site chrome) */}
      <Route path="/hub" element={<GuestHub />} />
      <Route path="/hub/:slug" element={<GuestHub />} />
      <Route path="/:slug/wifi" element={<WifiPage />} />
      <Route path="/:slug/guidebook" element={<HubGuidebook />} />
      <Route path="/:slug/local-guide" element={<HubLocalGuide />} />
      <Route path="/:slug/social" element={<SocialPage />} />
      <Route path="/:slug/book" element={<BookPage />} />

      {/* OAuth Callback Route */}
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Public Routes with Header/Footer */}
      <Route path="/*" element={
        <>
          <Header />
          <main className="flex-grow">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/properties" element={<Properties />} />
              <Route path="/property/:id" element={<PropertyDetail />} />
              <Route path="/book-now" element={<BookNow />} />
              <Route path="/book" element={<BookNow />} />
              <Route path="/booking-widget" element={<BookingWidget />} />
              <Route path="/about" element={<About />} />
              <Route path="/local-guide" element={<LocalGuide />} />
              <Route path="/local-guide/:id" element={<PropertyLocalGuide />} />
              <Route path="/house-rules" element={<HouseRules />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/blog" element={<Blog />} />
              {/* Booking Confirmation - for conversion tracking */}
              <Route path="/booking-confirmation" element={<BookingConfirmation />} />
              {/* SEO Location Pages */}
              <Route path="/locations" element={<LocationsIndex />} />
              <Route path="/locations/:slug" element={<LocationPage />} />
              {/* QR Codes Page */}
              <Route path="/qr-codes" element={<PropertyQRCodes />} />
              <Route path="/qr-signs" element={<QRSigns />} />
              {/* Guest Resources - QR code display for guests */}
              <Route path="/guest-resources" element={<GuestResources />} />
              <Route path="/guest-resources/:propertyId" element={<GuestResources />} />
              {/* User Favorites */}
              <Route path="/favorites" element={<Favorites />} />
              {/* Digital Guidebook - accessible via QR code */}
              <Route path="/guidebook" element={<Navigate to="/hub" replace />} />
              <Route path="/guidebook/:propertyId" element={<LegacyGuidebookRedirect />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
            </Routes>
          </main>
          <Footer />
          <EmailPopup />
        </>
      } />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <GuestAuthProvider>
        <div className="App min-h-screen flex flex-col">
          <BrowserRouter>
            <SEOHead />
            <AppRouter />
            <Toaster />
          </BrowserRouter>
        </div>
      </GuestAuthProvider>
    </AuthProvider>
  );
}

export default App;
